import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { TokenManager } from '../utils/tokenManager';
import paths from '../routes/paths';
import { Loading } from './common/Loading';

/**
 * 보호된 라우트 컴포넌트
 * 화면 렌더링 전에 인증 상태를 동기적으로 체크하여 매끄러운 리다이렉트 제공
 */
const ProtectedRoute = ({ children }) => {
    const location = useLocation();
    const [authState, setAuthState] = useState({
        isChecking: true,
        isAuthenticated: false,
        redirectTo: null,
        shouldShowLoading: true
    });

    useEffect(() => {
        const checkAuthentication = async () => {
            try {
                const currentPath = location.pathname;

                // 인증 관련 경로는 바로 통과
                const isAuthRoute = currentPath.startsWith('/authentication') ||
                    currentPath.startsWith('/oidc') ||
                    currentPath.startsWith('/auth') ||
                    currentPath.startsWith('/apply') ||
                    currentPath.startsWith('/sa');

                if (isAuthRoute) {
                    setAuthState({
                        isChecking: false,
                        isAuthenticated: true,
                        redirectTo: null,
                        shouldShowLoading: false
                    });
                    return;
                }

                // 토큰 존재 여부 확인
                const hasJwtToken = !!sessionStorage.getItem('token');
                const hasOidcToken = !!localStorage.getItem('oidc_access_token') ||
                    !!localStorage.getItem('access_token');
                const hasAnyToken = hasJwtToken || hasOidcToken;

                if (!hasAnyToken) {
                    // 토큰이 없으면 로그인 페이지로 리다이렉트
                    setAuthState({
                        isChecking: false,
                        isAuthenticated: false,
                        redirectTo: paths.cardLogin,
                        shouldShowLoading: false
                    });
                    return;
                }

                // SA 사용자 특별 처리
                const userRole = TokenManager.getUserRole();
                if (userRole === 'SA') {
                    if (currentPath.includes('/safedb/common/') || currentPath.startsWith('/safedb/common/')) {
                        // SA 유저가 common 경로 접근 시 siteToken 필요
                        const siteToken = sessionStorage.getItem('siteToken');
                        if (!siteToken || !TokenManager.isSiteTokenValid()) {
                            setAuthState({
                                isChecking: false,
                                isAuthenticated: false,
                                redirectTo: '/safedb/SA/adminSites',
                                shouldShowLoading: false
                            });
                            return;
                        }
                    } else if (currentPath.includes('/safedb/SA/') || currentPath.startsWith('/safedb/SA/')) {
                        // SA 경로 접근 시 siteToken 제거
                        const siteToken = sessionStorage.getItem('siteToken');
                        if (siteToken) {
                            sessionStorage.removeItem('siteToken');
                            console.log('SA 경로 접근 시 siteToken 제거됨');
                        }
                    }
                }

                // 토큰 유효성 검증
                try {
                    TokenManager.createAuthHeader(currentPath);
                    setAuthState({
                        isChecking: false,
                        isAuthenticated: true,
                        redirectTo: null,
                        shouldShowLoading: false
                    });
                } catch (error) {
                    // 토큰 오류 시 로그인 페이지로 리다이렉트
                    console.log('토큰 검증 실패:', error.message);

                    // 토큰 정리
                    sessionStorage.removeItem('token');
                    sessionStorage.removeItem('refreshToken');
                    sessionStorage.removeItem('siteToken');

                    setAuthState({
                        isChecking: false,
                        isAuthenticated: false,
                        redirectTo: paths.cardLogin,
                        shouldShowLoading: false
                    });
                }

            } catch (error) {
                console.error('인증 체크 중 오류:', error);
                setAuthState({
                    isChecking: false,
                    isAuthenticated: false,
                    redirectTo: paths.cardLogin,
                    shouldShowLoading: false
                });
            }
        };

        // 짧은 지연으로 로딩 화면 표시 (UX 개선)
        const timer = setTimeout(() => {
            checkAuthentication();
        }, 100);

        return () => clearTimeout(timer);
    }, [location.pathname]);

    // 인증 체크 중이면 로딩 표시
    if (authState.isChecking || authState.shouldShowLoading) {
        return <Loading />;
    }

    // 리다이렉트가 필요한 경우
    if (authState.redirectTo) {
        return <Navigate to={authState.redirectTo} replace />;
    }

    // 인증된 경우 자식 컴포넌트 렌더링
    if (authState.isAuthenticated) {
        return children;
    }

    // 기본적으로 로그인 페이지로 리다이렉트
    return <Navigate to={paths.cardLogin} replace />;
};

export default ProtectedRoute;
